import React, {useEffect, useState} from "react";
import {BeatLoader} from "react-spinners";
import {toast} from "react-toastify";
import {useStateValue} from "../../../states/StateProvider";
import {AES, enc} from "crypto-js";
import {useNavigate} from "react-router";
import {documentAPI, initializeiFExAuth} from "../../../Api/ifex";
import DocumentUploadModal from "./DocumentUploadModal";
import DocumentEditModal from "./DocumentEditModal";
import ConfirmationModal from "../../../components/modal/ConfirmationModal";
import Pagination from "../../../components/pagination";
import {FiEdit2, FiTrash2, FiPlus, FiSearch, FiFilter, FiDownload, FiUpload, FiFileText} from "react-icons/fi";

const DocumentManagement = () => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dataLoading, setDataLoading] = useState(false);
    const [{secretKey}, dispatch] = useStateValue();
    const navigate = useNavigate();

    // Data state
    const [documents, setDocuments] = useState([]);
    const [pagination, setPagination] = useState({});
    const [modules, setModules] = useState([]);

    // Modal state
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Filter state
    const [filters, setFilters] = useState({
        search: '',
        is_active: '', // '' = all, '1' = active, '0' = inactive
        module: '', // DGG, RUV, etc.
    });

    // Sorting state
    const [sorting, setSorting] = useState({
        field: 'sequence',
        direction: 'asc'
    });

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(15);

    // Check if user is Super-Admin
    const isSuperAdmin = user?.isSAdmin === '1' || user?.role === 'Super Admin';

    useEffect(() => {
        try {
            const decryptedBytes = localStorage.getItem('user') ? AES.decrypt(localStorage.getItem('user'), secretKey) : false;
            const userData = JSON.parse(decryptedBytes.toString(enc.Utf8));

            if (!userData) {
                navigate('/anmeldung');
                return;
            }

            setUser(userData);
            setLoading(false);
        } catch (e) {
            console.error('Authentication error:', e);
            window.location.replace('/anmeldung');
        }
    }, [secretKey, navigate]);

    useEffect(() => {
        if (user) {
            fetchDocuments();
            fetchModules();
        }
    }, [user, filters, sorting, currentPage, perPage]);

    const fetchDocuments = async () => {
        setDataLoading(true);
        try {
            // Initialize iFEx authentication first
            const authSuccess = await initializeiFExAuth();
            if (!authSuccess) {
                toast.error('iFEx Authentifizierung fehlgeschlagen');
                setDataLoading(false);
                return;
            }

            const params = {
                page: currentPage,
                per_page: perPage,
                sort_field: sorting.field,
                sort_direction: sorting.direction,
                ...(filters.search && { search: filters.search }),
                ...(filters.is_active !== '' && { is_active: filters.is_active }),
                ...(filters.module && { module: filters.module }),
            };

            const response = await documentAPI.getAll(params);

            if (response.data.success) {
                setDocuments(response.data.data);
                setPagination(response.data.meta);
            }
        } catch (error) {
            console.error('Error fetching documents:', error);
            toast.error('Fehler beim Laden der Dokumente');
        } finally {
            setDataLoading(false);
        }
    };

    const fetchModules = async () => {
        try {
            // Initialize iFEx authentication first
            const authSuccess = await initializeiFExAuth();
            if (!authSuccess) {
                toast.error('iFEx Authentifizierung fehlgeschlagen');
                return;
            }

            const response = await documentAPI.getModules();
            if (response.data.success) {
                setModules(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching modules:', error);
        }
    };

    const handleSort = (field) => {
        setSorting(prev => ({
            field,
            direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const handleEdit = (document) => {
        setSelectedDocument(document);
        setShowEditModal(true);
    };

    const handleDelete = (document) => {
        setSelectedDocument(document);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        setDeleteLoading(true);
        try {
            // Initialize iFEx authentication first
            const authSuccess = await initializeiFExAuth();
            if (!authSuccess) {
                toast.error('iFEx Authentifizierung fehlgeschlagen');
                setDeleteLoading(false);
                return;
            }

            await documentAPI.delete(selectedDocument.id);
            toast.success('Dokument erfolgreich gelöscht');
            setShowDeleteModal(false);
            setSelectedDocument(null);
            fetchDocuments();
        } catch (error) {
            console.error('Error deleting document:', error);
            if (error.response?.status === 409) {
                toast.error('Dokument kann nicht gelöscht werden, da es bereits für Generierungen verwendet wurde');
            } else {
                toast.error('Fehler beim Löschen des Dokuments');
            }
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleDownload = async (document) => {
        try {
            // Initialize iFEx authentication first
            const authSuccess = await initializeiFExAuth();
            if (!authSuccess) {
                toast.error('iFEx Authentifizierung fehlgeschlagen');
                return;
            }

            const response = await documentAPI.download(document.id);

            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = window.createElement('a');
            link.href = url;
            link.setAttribute('download', document.file_name);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast.success('Dokument wird heruntergeladen');
        } catch (error) {
            console.error('Error downloading document:', error);
            toast.error('Fehler beim Herunterladen des Dokuments');
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setCurrentPage(1); // Reset to first page when filtering
    };

    const clearFilters = () => {
        setFilters({
            search: '',
            is_active: '',
            module: '',
        });
        setCurrentPage(1);
    };

    const SortIcon = ({ field }) => {
        if (sorting.field !== field) return <span className="text-grey ml-1">↕</span>;
        return sorting.direction === 'asc' ? <span className="ml-1">↑</span> : <span className="ml-1">↓</span>;
    };

    if (loading) {
        return (
            <div className='dashboardContainer flex justify-center items-center'>
                <BeatLoader color="#3A46A9" size={15} />
            </div>
        );
    }

    return (
        <div className='dashboardContainer'>
            {/* Header */}
            <div className='flex justify-between items-center content-center pb-5'>
                <h2 className='text-2xl font-bold text-text text-left'>Dokumenten-Verwaltung</h2>
                {isSuperAdmin && (
                    <button
                        onClick={() => setShowUploadModal(true)}
                        className='inline-flex items-center px-6 py-3 border border-transparent shadow-md text-sm font-semibold rounded-lg text-white bg-mainBlue hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mainBlue transition-all'
                    >
                        <FiPlus className="mr-2" /> Neues Dokument hinzufügen
                    </button>
                )}
            </div>

            {/* Filters */}
            <div className='bg-white rounded-lg shadow-lg border border-borderBlue mb-4 p-6'>
                <div className='flex items-center gap-2 mb-4'>
                    <FiFilter className="text-mainBlue" size={20} />
                    <h3 className='text-lg font-semibold text-text'>Filter</h3>
                </div>
                <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'>
                    {/* Search */}
                    <div className='relative'>
                        <FiSearch className='absolute left-3 top-1/2 transform -translate-y-1/2 text-grey' />
                        <input
                            type='text'
                            placeholder='Suche nach Dokumentname...'
                            className='w-full pl-10 pr-3 py-2 border border-borderBlue rounded text-text focus:border-mainBlue focus:outline-none'
                            value={filters.search}
                            onChange={(e) => handleFilterChange('search', e.target.value)}
                        />
                    </div>

                    {/* Active Status */}
                    <select
                        className='w-full border border-borderBlue rounded px-3 py-2 text-text focus:border-mainBlue focus:outline-none'
                        value={filters.is_active}
                        onChange={(e) => handleFilterChange('is_active', e.target.value)}
                    >
                        <option value=''>Alle Status</option>
                        <option value='1'>Aktiv</option>
                        <option value='0'>Inaktiv</option>
                    </select>

                    {/* Module Filter */}
                    <select
                        className='w-full border border-borderBlue rounded px-3 py-2 text-text focus:border-mainBlue focus:outline-none'
                        value={filters.module}
                        onChange={(e) => handleFilterChange('module', e.target.value)}
                    >
                        <option value=''>Alle Module</option>
                        {modules.map((module, idx) => (
                            <option key={idx} value={module}>{module}</option>
                        ))}
                    </select>

                    {/* Clear Filters */}
                    <button
                        onClick={clearFilters}
                        className='px-4 py-2 bg-whiteDark hover:bg-offWhite text-grey rounded transition'
                    >
                        Filter zurücksetzen
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className='bg-white rounded-lg shadow-lg border border-borderBlue px-6 py-4'>
                {dataLoading ? (
                    <div className='flex justify-center items-center h-72'>
                        <BeatLoader size={12} color="#3A46A9" />
                    </div>
                ) : documents.length > 0 ? (
                    <>
                        <div className='overflow-x-auto'>
                            <table className='w-full' style={{fontSize: "14px"}}>
                                <thead>
                                    <tr className='border-y border-silver'>
                                        <th className='text-left py-2 text-text font-semibold pr-3 cursor-pointer select-none' onClick={() => handleSort('document_name')}>
                                            Formular <SortIcon field='document_name' />
                                        </th>
                                        <th className='text-left py-2 text-text font-semibold pr-3 cursor-pointer select-none' onClick={() => handleSort('sequence')}>
                                            PDF (Sequenz) <SortIcon field='sequence' />
                                        </th>
                                        <th className='text-left py-2 text-text font-semibold pr-3 cursor-pointer select-none' onClick={() => handleSort('is_active')}>
                                            Aktiv <SortIcon field='is_active' />
                                        </th>
                                        <th className='text-left py-2 text-text font-semibold pr-3'>Modul</th>
                                        <th className='text-left py-2 text-text font-semibold pr-3'>Dateiname</th>
                                        <th className='text-left py-2 text-text font-semibold pr-3 cursor-pointer select-none' onClick={() => handleSort('created_at')}>
                                            Upload Datum <SortIcon field='created_at' />
                                        </th>
                                        <th className='text-left py-2 text-text font-semibold pr-3'>Upload von</th>
                                        <th className='text-left py-2 text-text font-semibold pr-3'>Letzter Zugriff</th>
                                        <th className='text-left py-2 text-text font-semibold pr-3'>Aktionen</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {documents.map((document) => (
                                        <tr
                                            key={document.id}
                                            className='border-y border-silver hover:bg-offWhite cursor-pointer transition'
                                            onClick={() => handleEdit(document)}
                                        >
                                            <td className='py-2 text-text font-medium pr-3'>{document.document_name}</td>
                                            <td className='py-2 text-text font-light pr-3'>{document.sequence || '-'}</td>
                                            <td className='py-2 font-light pr-3'>
                                                <span className={`px-2 py-1 rounded text-xs ${document.is_active ? 'bg-complete text-green' : 'bg-redLight text-red'}`}>
                                                    {document.is_active ? 'Aktiv' : 'Inaktiv'}
                                                </span>
                                            </td>
                                            <td className='py-2 text-text font-light pr-3'>{document.module || '-'}</td>
                                            <td className='py-2 text-text font-light pr-3'>
                                                <div className='flex items-center gap-1'>
                                                    <FiFileText className="text-mainBlue" size={14} />
                                                    <span className='truncate max-w-xs' title={document.file_name}>
                                                        {document.file_name}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className='py-2 text-text font-light pr-3'>{document.created_at ? new Date(document.created_at).toLocaleDateString('de-DE') : '-'}</td>
                                            <td className='py-2 text-text font-light pr-3'>{document.uploaded_by || '-'}</td>
                                            <td className='py-2 text-text font-light pr-3'>{document.last_accessed_at ? new Date(document.last_accessed_at).toLocaleDateString('de-DE') : '-'}</td>
                                            <td className='py-2 font-light pr-3' onClick={(e) => e.stopPropagation()}>
                                                <div className='flex gap-3'>
                                                    <button
                                                        onClick={() => handleDownload(document)}
                                                        className='text-green hover:text-opacity-80 transition'
                                                        title='Vorlage herunterladen'
                                                    >
                                                        <FiDownload size={16} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleEdit(document)}
                                                        className='text-mainBlue hover:text-lightBlue transition'
                                                        title='Bearbeiten'
                                                    >
                                                        <FiEdit2 size={16} />
                                                    </button>
                                                    {isSuperAdmin && (
                                                        <button
                                                            onClick={() => handleDelete(document)}
                                                            className='text-cancel hover:text-red transition'
                                                            title='Löschen'
                                                        >
                                                            <FiTrash2 size={16} />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {pagination && pagination.total > 0 && (
                            <div className='flex justify-between items-center mt-6 pt-4 border-t border-borderBlue'>
                                <div className='text-sm text-grey'>
                                    {pagination.from} bis {pagination.to} von {pagination.total} Einträge
                                </div>
                                <div className='flex items-center gap-2'>
                                    <span className='text-sm text-grey'>Einträge anzeigen:</span>
                                    <select
                                        className='border border-borderBlue rounded px-2 py-1 text-sm text-text focus:border-mainBlue focus:outline-none'
                                        value={perPage}
                                        onChange={(e) => {
                                            setPerPage(Number(e.target.value));
                                            setCurrentPage(1);
                                        }}
                                    >
                                        <option value="10">10</option>
                                        <option value="15">15</option>
                                        <option value="25">25</option>
                                        <option value="50">50</option>
                                        <option value="100">100</option>
                                    </select>
                                </div>
                                {pagination.last_page > 1 && (
                                    <Pagination
                                        currentPage={currentPage}
                                        totalCount={pagination.total}
                                        pageSize={perPage}
                                        onPageChange={page => setCurrentPage(page)}
                                    />
                                )}
                            </div>
                        )}
                    </>
                ) : (
                    <div className='text-center py-16 bg-offWhite rounded'>
                        <div className='inline-flex items-center justify-center w-16 h-16 bg-mainBlue bg-opacity-10 rounded-full mb-4'>
                            <FiFileText className='h-8 w-8 text-mainBlue' />
                        </div>
                        <h3 className='text-lg font-semibold text-text mb-2'>Keine Dokumente gefunden</h3>
                        <p className='text-base text-grey mb-6'>
                            {filters.search || filters.is_active || filters.module
                                ? 'Versuchen Sie, die Filter anzupassen oder zurückzusetzen.'
                                : 'Laden Sie Ihr erstes Dokumententemplate hoch, um zu beginnen.'}
                        </p>
                        {isSuperAdmin && !filters.search && !filters.is_active && !filters.module && (
                            <button
                                onClick={() => setShowUploadModal(true)}
                                className='inline-flex items-center px-6 py-3 border border-transparent shadow-md text-sm font-semibold rounded-lg text-white bg-mainBlue hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mainBlue transition-all'
                            >
                                Erstes Dokument hochladen
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Modals */}
            {showUploadModal && (
                <DocumentUploadModal
                    visible={showUploadModal}
                    onClose={() => setShowUploadModal(false)}
                    onSuccess={() => {
                        setShowUploadModal(false);
                        fetchDocuments();
                    }}
                    user={user}
                    modules={modules}
                />
            )}

            {showEditModal && selectedDocument && (
                <DocumentEditModal
                    visible={showEditModal}
                    document={selectedDocument}
                    onClose={() => {
                        setShowEditModal(false);
                        setSelectedDocument(null);
                    }}
                    onSuccess={() => {
                        setShowEditModal(false);
                        setSelectedDocument(null);
                        fetchDocuments();
                    }}
                    user={user}
                    isSuperAdmin={isSuperAdmin}
                    modules={modules}
                />
            )}

            {showDeleteModal && selectedDocument && (
                <ConfirmationModal
                    visible={showDeleteModal}
                    title='Dokument löschen?'
                    message={`Möchten Sie das Dokument "${selectedDocument.document_name}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`}
                    confirmText='Löschen'
                    cancelText='Abbrechen'
                    onConfirm={confirmDelete}
                    onCancel={() => {
                        setShowDeleteModal(false);
                        setSelectedDocument(null);
                    }}
                    loading={deleteLoading}
                />
            )}
        </div>
    );
};

export default DocumentManagement;
